import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { itemIdOf } from "../../../../api/content/processDefinitions";
import type { WorkflowListing, WorkflowSummary } from "../../../../api/content/workflowsApi";
import { appPaths } from "../../../../shared/routing/appPaths";
import AppButton from "../../../../ui/buttons/AppButton";
import EmptyState from "../../../../ui/feedback/EmptyState";
import StatusMessage from "../../../../ui/feedback/StatusMessage";
import ChoiceCard from "../../../../ui/forms/ChoiceCard";
import FieldGroup from "../../../../ui/forms/FieldGroup";
import WorkflowGroupList from "../../../workflows/list/WorkflowGroupList";
import { workflowInputsText } from "./workflowInputsText";

interface WorkflowChoiceProps {
  projectId: string;
  /** The content version's Workflows that fit the start; `null` before the project's first content version. */
  listing: WorkflowListing | null;
  /** Shown in place of the list while the fitting Workflows load or when that fails; `null` once they are known. */
  pending: ReactNode;
  /** Started from documents: the list holds only the Workflows that take them (F123). */
  fitted: boolean;
  /** The chosen Workflow, or `null` for a free Thread. */
  workflow: WorkflowSummary | null;
  /** The person came with this Workflow chosen ("Run this Workflow…"). */
  cameWithWorkflow: boolean;
  requestKey: string;
  disabled: boolean;
  /** A Workflow's item ID, or `""` for a free Thread. */
  onPick: (item: string) => void;
  /** Under each card's text: what a Workflow does with what the Thread has, at a switch (F126); none at a start. */
  detailOf?: (candidate: WorkflowSummary) => ReactNode;
  /** A free Thread is offered after the list; a switch leaves it out. */
  offersFree?: boolean;
  /** What an empty list says, in place of the start's sentence. */
  emptyText?: string;
}

/**
 * Which Workflow the run follows (F52). A chosen Workflow, chosen here or before ("Run this
 * Workflow…"), closes the list to its card, so the focus moves on to its inputs (F98);
 * "Choose another Workflow…" opens the list again. The list is every Workflow list's
 * (`WorkflowGroupList`, F97, F111), then a free Thread (`""`). Each card says what the
 * Workflow takes to start (F28, F123), and at a switch what it uses and still misses (F126).
 */
export default function WorkflowChoice({ projectId, listing, pending, fitted, workflow, cameWithWorkflow, requestKey, disabled, onPick, detailOf, offersFree = true, emptyText }: WorkflowChoiceProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const [listOpen, setListOpen] = useState(false);

  if (workflow !== null && !listOpen) {
    const takes = workflowInputsText(workflow.inputs, t);
    return (
      <section className="space-y-2" aria-labelledby={headingId}>
        <h3 id={headingId} className="app-title text-sm font-semibold">{t("threads.prepare.run.workflow")}</h3>
        <div className="app-choice-card app-choice-card-selected flex items-start gap-3 p-3">
          <span className="min-w-0 space-y-0.5">
            <span className="app-choice-title block text-sm">{`${workflow.name} · ${t("threads.prepare.run.steps", { count: workflow.stepCount })}`}</span>
            {workflow.description === "" ? null : <span className="app-choice-description block text-xs">{workflow.description}</span>}
            {takes === null ? null : <span className="app-choice-description block text-xs">{takes}</span>}
            {detailOf === undefined ? null : <span className="app-choice-description block text-xs">{detailOf(workflow)}</span>}
          </span>
          <Link to={appPaths.workflow(workflow.reference)} className="app-link ml-auto shrink-0 text-xs">{t("threads.prepare.run.openWorkflow")}</Link>
        </div>
        <AppButton variant="secondary" size="xs" disabled={disabled} onClick={() => { setListOpen(true); }}>{t("threads.prepare.run.chooseAnother")}</AppButton>
      </section>
    );
  }
  // Chosen before, but not among this content version's Workflows: say so, never fall back to a free Thread unseen.
  const missing = cameWithWorkflow && workflow === null && !listOpen;
  const card = (candidate: WorkflowSummary, marks: ReactNode) => (
    <WorkflowCard
      key={candidate.reference}
      candidate={candidate}
      name={`${requestKey}-workflow`}
      checked={workflow?.reference === candidate.reference}
      marks={marks}
      detail={detailOf?.(candidate) ?? null}
      onChoose={() => { setListOpen(false); onPick(itemIdOf(candidate.reference)); }}
    />
  );
  return (
    <FieldGroup legend={t("threads.prepare.run.workflow")} className="space-y-3" disabled={disabled}>
      {missing ? (
        <StatusMessage tone="warning" title={t("threads.prepare.run.chosenMissingTitle")}>
          <p>{t("threads.prepare.run.chosenMissingBody")}</p>
        </StatusMessage>
      ) : null}
      {pending ?? <WorkflowGroupList projectId={projectId} workflows={listing?.workflows ?? []} row={card} current={workflow?.reference ?? null} />}
      {offersFree ? (
        <ChoiceCard
          name={`${requestKey}-workflow`}
          checked={workflow === null && !missing}
          onChoose={() => { setListOpen(true); onPick(""); }}
          title={t("threads.prepare.run.free")}
          description={t("threads.prepare.run.freeHint")}
        />
      ) : null}
      {listing === null ? (
        <StatusMessage tone="info" title={t("threads.prepare.run.noContentTitle")}>
          <p>{t("threads.prepare.run.noContentBody")}</p>
        </StatusMessage>
      ) : pending === null && listing.workflows.length === 0 ? (
        <EmptyState size="small">{emptyText ?? t(fitted ? "threads.prepare.run.noFittingWorkflows" : "threads.prepare.run.noWorkflows")}</EmptyState>
      ) : null}
    </FieldGroup>
  );
}

interface WorkflowCardProps {
  candidate: WorkflowSummary;
  name: string;
  checked: boolean;
  /** Its origin mark and star, from the list. */
  marks: ReactNode;
  /** What it does with what the Thread has (F126); `null` at a start. */
  detail: ReactNode;
  onChoose: () => void;
}

/** One Workflow to choose: its marks (what it is part of, its origin, its star) under its text, and a link to its definition. */
function WorkflowCard({ candidate, name, checked, marks, detail, onChoose }: WorkflowCardProps) {
  const { t } = useTranslation();
  const takes = workflowInputsText(candidate.inputs, t);
  return (
    <ChoiceCard
      name={name}
      checked={checked}
      onChoose={onChoose}
      title={`${candidate.name} · ${t("threads.prepare.run.steps", { count: candidate.stepCount })}`}
      description={
        <>
          {candidate.description}
          {takes === null ? null : <span className="mt-1 block">{takes}</span>}
          {detail === null ? null : <span className="mt-1 block">{detail}</span>}
          {/* Below the text, wrapping, so a long "Part of …" never squeezes the name (Sol S5). */}
          <span className="mt-1 flex min-w-0 flex-wrap items-center gap-1">{marks}</span>
        </>
      }
      aside={<Link to={appPaths.workflow(candidate.reference)} className="app-link text-xs">{t("threads.prepare.run.openWorkflow")}</Link>}
    />
  );
}
